/**
 * Enhanced Frictionless Authentication Page
 * Supports: Magic Link, Phone/SMS, Email/Password, Google
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
} from '@mui/material';
import {
  Email,
  Phone,
  Google,
  Send,
  MailOutline,
  CheckCircle,
  Lock,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { AuthEnhanced } from '@/services/authEnhanced';
import { useAuthStore } from '@/store/authStore';
import LanguageSwitcher from '@/components/LanguageSwitcher';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const EnhancedLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuthStore();

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Magic Link State
  const [magicEmail, setMagicEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Phone State
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  // Email/Password State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);

  // Check if arriving from magic link
  useEffect(() => {
    const checkMagicLink = async () => {
      if (location.pathname === '/auth/verify') {
        try {
          setLoading(true);
          const user = await AuthEnhanced.completeMagicLinkSignIn(window.location.href);
          setUser(user);
          navigate('/dashboard');
        } catch (err: any) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }
    };
    checkMagicLink();
  }, [location, navigate, setUser]);

  // Initialize reCAPTCHA for phone auth
  useEffect(() => {
    if (tabValue === 1) {
      AuthEnhanced.setupRecaptcha('recaptcha-container');
    }
  }, [tabValue]);

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await AuthEnhanced.sendMagicLink(magicEmail);
      setMagicLinkSent(true);
      setSuccess('Check your email! We sent you a magic sign-in link.');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!codeSent) {
        await AuthEnhanced.sendPhoneVerificationCode(phoneNumber);
        setCodeSent(true);
        setSuccess('Verification code sent to your phone!');
      } else {
        const user = await AuthEnhanced.verifyPhoneCode(verificationCode);
        setUser(user);
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message);
      setCodeSent(false);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let user;
      if (isSignUp) {
        user = await AuthEnhanced.signUpWithEmail(email, password, {
          firstName: '',
          lastName: '',
        });
        setUser(user);
        navigate('/onboarding');
      } else {
        user = await AuthEnhanced.signInWithEmail(email, password);
        setUser(user);
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);

    try {
      const user = await AuthEnhanced.quickGoogleSignIn();
      setUser(user);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2,
        position: 'relative',
      }}
    >
      <Box sx={{ position: 'absolute', top: 24, right: 24 }}>
        <LanguageSwitcher />
      </Box>

      <Card
        sx={{
          maxWidth: 480,
          width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 'bold' }}
            >
              souk.immo
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to your account
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
              <Chip label="Fast" size="small" color="primary" variant="outlined" />
              <Chip label="Secure" size="small" color="success" variant="outlined" />
              <Chip label="Frictionless" size="small" color="secondary" variant="outlined" />
            </Stack>
          </Box>

          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Quick Google Sign-in (Most Frictionless) */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleGoogleSignIn}
            disabled={loading}
            startIcon={<Google />}
            sx={{
              mb: 3,
              py: 1.5,
              background: 'linear-gradient(45deg, #4285f4 30%, #34a853 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #3367d6 30%, #2d8e47 90%)',
              }
            }}
          >
            Continue with Google
          </Button>

          <Divider sx={{ mb: 3 }}>
            <Typography variant="caption" color="text.secondary">
              or choose your method
            </Typography>
          </Divider>

          {/* Authentication Tabs */}
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="fullWidth"
            sx={{ mb: 2 }}
          >
            <Tab icon={<MailOutline />} label="Magic Link" />
            <Tab icon={<Phone />} label="Phone" />
            <Tab icon={<Lock />} label="Password" />
          </Tabs>

          {/* Tab 0: Magic Link (Passwordless Email) */}
          <TabPanel value={tabValue} index={0}>
            {!magicLinkSent ? (
              <form onSubmit={handleMagicLinkSubmit}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Get a magic link sent to your email. No password needed!
                </Typography>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={magicEmail}
                  onChange={(e) => setMagicEmail(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                >
                  {loading ? 'Sending...' : 'Send Magic Link'}
                </Button>
              </form>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Check Your Email
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  We sent a magic link to <strong>{magicEmail}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Click the link in your email to sign in instantly
                </Typography>
                <Button
                  variant="text"
                  onClick={() => {
                    setMagicLinkSent(false);
                    setSuccess(null);
                  }}
                >
                  Try different email
                </Button>
              </Box>
            )}
          </TabPanel>

          {/* Tab 1: Phone/SMS */}
          <TabPanel value={tabValue} index={1}>
            <form onSubmit={handlePhoneSubmit}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sign in with your phone number. We'll send you a verification code.
              </Typography>

              {!codeSent ? (
                <>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+212 6XX XX XX XX"
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                    helperText="Include country code (e.g., +212 for Morocco)"
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                  >
                    {loading ? 'Sending...' : 'Send Code'}
                  </Button>
                </>
              ) : (
                <>
                  <TextField
                    fullWidth
                    label="Verification Code"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="123456"
                    required
                    inputProps={{ maxLength: 6 }}
                    sx={{ mb: 2 }}
                    helperText={`Code sent to ${phoneNumber}`}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                  >
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                  </Button>
                  <Button
                    fullWidth
                    variant="text"
                    size="small"
                    onClick={() => {
                      setCodeSent(false);
                      setVerificationCode('');
                    }}
                    sx={{ mt: 1 }}
                  >
                    Try different number
                  </Button>
                </>
              )}
            </form>
          </TabPanel>

          {/* Tab 2: Email/Password (Traditional) */}
          <TabPanel value={tabValue} index={2}>
            <form onSubmit={handleEmailPasswordSubmit}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mb: 2 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  isSignUp ? 'Sign Up' : 'Sign In'
                )}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="text"
                  onClick={() => setIsSignUp(!isSignUp)}
                  disabled={loading}
                >
                  {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
                </Button>
              </Box>
            </form>
          </TabPanel>

          {/* reCAPTCHA container (invisible) */}
          <div id="recaptcha-container"></div>

          {/* Footer */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EnhancedLoginPage;
